import { faXmark } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { type ReactNode } from 'react';
import { Button, Dialog, Heading, Modal as RAModal, ModalOverlay, Separator } from 'react-aria-components';
import { twMerge } from 'tailwind-merge';

interface Props {
  isOpen: boolean;
  title: string | ReactNode;
  description?: string;
  children?: ReactNode;
  className?: string;
  hideCloseButton?: boolean;
  isDismissable?: boolean;
  onCancel?: () => void;
  onOpenChange?: (isOpen: boolean) => void;
}

export default function Modal({
  isOpen,
  title,
  description,
  children,
  className,
  hideCloseButton = false,
  isDismissable = true,
  onCancel,
  onOpenChange,
}: Props) {
  return (
    <ModalOverlay
      isDismissable={isDismissable}
      isOpen={isOpen}
      aria-label="modal"
      onOpenChange={(isOpen) => {
        if (!isOpen) onCancel?.();
        onOpenChange?.(isOpen);
      }}
      className={({ isEntering, isExiting }) =>
        twMerge(
          'fixed inset-0 z-10 flex min-h-full items-center justify-center overflow-y-auto bg-[#00000080] p-6 text-center',
          isEntering ? 'duration-300 ease-out animate-in fade-in' : '',
          isExiting ? 'duration-200 ease-in animate-out fade-out' : '',
        )
      }
    >
      <RAModal
        aria-labelledby="modal-title"
        aria-describedby="modal-description"
        onOpenChange={onOpenChange}
        className={({ isEntering, isExiting }) =>
          twMerge(
            'flex w-full max-w-md flex-col overflow-hidden rounded-[4px] bg-white text-left align-middle shadow-xl',
            isEntering ? 'duration-300 ease-out animate-in zoom-in-95' : '',
            isExiting ? 'duration-200 ease-in animate-out zoom-out-95' : '',
            className,
          )
        }
      >
        <Dialog role="alertdialog" className="relative m-0 flex min-h-0 flex-1 flex-col outline-none">
          {({ close }) => (
            <div className="flex min-h-0 flex-1 flex-col">
              <div className="flex shrink-0 items-center justify-between p-6">
                <Heading slot="title" className="my-0 text-[16px] font-[500] leading-6 tracking-[-0.25px] text-black">
                  {title}
                </Heading>
                {!hideCloseButton && (
                  <Button
                    className="p-2 text-slate-500 hover:text-slate-600"
                    onPress={() => {
                      close(); // will trigger ModalOverlay onOpenChange with isOpen = false
                    }}
                  >
                    <FontAwesomeIcon icon={faXmark} className="h-4 w-4 cursor-pointer text-[#00000066]" />
                  </Button>
                )}
              </div>
              <Separator className="m-0 shrink-0" />
              <div className="min-h-0 flex-1 overflow-y-auto p-6">
                {description && <p className="m-0 text-slate-500">{description}</p>}
                {children && <div className="text-slate-500">{children}</div>}
              </div>
            </div>
          )}
        </Dialog>
      </RAModal>
    </ModalOverlay>
  );
}
